import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from '@mui/material/Skeleton';
import WavesurferPlayer from '@wavesurfer/react';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import ForwardIcon from '@mui/icons-material/FastForward';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';

import { mmss } from './utils';
import './regions.css';

const SPEEDS = [1, 1.25, 1.5, 2];
const regions = RegionsPlugin.create();

const WAVE_OPTIONS = {
    autoplay: false,
    backend: 'MediaElement',
    dragToSeek: true,
    cursorColor: '#C8354A',
    cursorWidth: 2,
    waveColor: '#9DD3E8',
    progressColor: '#0089B8',
    hideScrollbar: true,
    mediaControls: false,
    normalize: true,
    splitChannels: true,
    plugins: [regions],
};

const Playlist = ({
    url,
    loading,
    duration: durationHint,
    regionsData,
    chunks,
    labels,
    flagged,
    onFlag,
    onTimeupdate,
    onPlayed,
    onPrev,
    onNext,
}) => {
    const [ws, setWs] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [time, setTime] = useState(0);
    const [duration, setDuration] = useState(durationHint || 0);
    const [speed, setSpeed] = useState(1);
    const [volume, setVolume] = useState(0.8);
    const [ready, setReady] = useState(false);
    const onTimeupdateRef = useRef(onTimeupdate);
    const onPlayedRef = useRef(onPlayed);
    onTimeupdateRef.current = onTimeupdate;
    onPlayedRef.current = onPlayed;

    const silences = useMemo(() => {
        const list = (regionsData && regionsData.silence) || [];
        return list.filter((el) => el && (el.endTime - el.startTime) >= 5);
    }, [regionsData]);

    useEffect(() => {
        setReady(false);
        setPlaying(false);
        setTime(0);
        setWs(null);
        setDuration(durationHint || 0);
    }, [url, durationHint]);

    useEffect(() => {
        if (ws) ws.setPlaybackRate(speed);
    }, [ws, speed]);

    useEffect(() => {
        if (ws) ws.setVolume(volume);
    }, [ws, volume]);

    useEffect(() => {
        if (!ws || !ready) return undefined;
        try { regions.clearRegions(); } catch (e) { /* empty */ }
        (chunks || []).forEach((elem) => {
            regions.addRegion({
                start: elem.startTime,
                end: elem.endTime,
                channelIdx: elem.channel,
                color: `rgb(244 67 54 / ${elem.avgTonal || 0})`,
                drag: false,
                resize: false,
            });
        });
        silences.forEach((elem) => {
            regions.addRegion({
                start: elem.startTime,
                end: elem.endTime,
                content: `${Math.round(elem.endTime - elem.startTime)}с`,
                color: 'rgba(200, 53, 74, 0.16)',
                drag: false,
                resize: false,
            });
        });
        ((regionsData && regionsData.simultaneous) || []).forEach((elem) => {
            regions.addRegion({
                id: 'simultaneous',
                start: elem.startTime,
                end: elem.startTime,
                channelIdx: elem.actor ? (elem.actor === 1 ? 0 : 1) : null,
                color: '#f50057',
                drag: false,
                resize: false,
            });
        });
        return undefined;
    }, [ws, ready, chunks, silences, regionsData]);

    const onReady = useCallback((instance) => {
        setWs(instance);
        setReady(true);
        setDuration(instance.getDuration() || 0);
    }, []);

    const onTime = useCallback((instance, t) => {
        setTime(t);
        if (onTimeupdateRef.current) onTimeupdateRef.current(t);
    }, []);

    const onPlay = useCallback(() => {
        setPlaying(true);
        if (onPlayedRef.current) onPlayedRef.current();
    }, []);

    const onPause = useCallback(() => setPlaying(false), []);
    const onFinish = useCallback(() => setPlaying(false), []);

    const seekBy = (delta) => {
        if (!ws) return;
        const next = Math.max(0, Math.min(duration, ws.getCurrentTime() + delta));
        ws.setTime(next);
        setTime(next);
    };

    const toggle = () => {
        if (!ws) return;
        ws.playPause();
    };

    useEffect(() => {
        const onKey = (e) => {
            const tag = e.target && e.target.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (e.code === 'Space') {
                e.preventDefault();
                if (ws) ws.playPause();
            }
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                if (ws) {
                    const next = Math.max(0, Math.min(duration, ws.getCurrentTime() + 5));
                    ws.setTime(next);
                    setTime(next);
                }
            }
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                if (ws) {
                    const next = Math.max(0, Math.min(duration, ws.getCurrentTime() - 5));
                    ws.setTime(next);
                    setTime(next);
                }
            }
            if (e.key === 'ArrowDown' && onNext) {
                e.preventDefault();
                onNext();
            }
            if (e.key === 'ArrowUp' && onPrev) {
                e.preventDefault();
                onPrev();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [ws, duration, onNext, onPrev]);

    const step = duration > 240 ? 60 : duration > 120 ? 30 : 20;
    const ticks = [];
    if (duration > 0) {
        for (let t = 0; t <= duration; t += step) ticks.push(t);
    }

    return (
        <>
            <div className="pc-ls-wavewrap">
                <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
                    <div style={{ width: 82, flex: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div className="pc-ls-ch__lbl" style={{ height: 62 }}>
                            <b><i style={{ background: 'var(--accent)' }} />Оператор</b>
                            <span>{(labels && labels.op) || '—'}</span>
                        </div>
                        <div className="pc-ls-ch__lbl" style={{ height: 62 }}>
                            <b><i style={{ background: 'var(--ok)' }} />Клиент</b>
                            <span>{(labels && labels.client) || '—'}</span>
                        </div>
                    </div>
                    <div className="pc-ls-wave">
                        {(loading || (url && !ready)) && (
                            <div className="pc-ls-wave-stub">
                                <Skeleton className="pc-ls-wave-stub__bar" variant="rectangular" />
                                <Skeleton className="pc-ls-wave-stub__bar" variant="rectangular" />
                            </div>
                        )}
                        {url ? (
                            <div id="waveform" className="pc-ls-wave__ws" style={{ opacity: ready ? 1 : 0, minHeight: ready ? 124 : 0 }}>
                                <WavesurferPlayer
                                    url={url}
                                    height={62}
                                    onReady={onReady}
                                    onTimeupdate={onTime}
                                    onPlay={onPlay}
                                    onPause={onPause}
                                    onFinish={onFinish}
                                    {...WAVE_OPTIONS}
                                />
                            </div>
                        ) : loading ? null : (
                            <div className="pc-ls-empty" style={{ padding: 18 }}>
                                <b>Нет файла записи</b>
                                Медиафайл для этого звонка недоступен.
                            </div>
                        )}
                    </div>
                </div>
                <div className="pc-ls-ruler">
                    {ticks.map((t) => (
                        <React.Fragment key={t}>
                            <i style={{ left: `${(t / duration) * 100}%` }} />
                            <span style={{ left: `${(t / duration) * 100}%` }}>{mmss(t)}</span>
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div className="pc-ls-transport">
                <button type="button" className="pc-ls-btn pc-ls-btn--ico" title="Назад 15 с" onClick={() => seekBy(-15)} disabled={!ws}>
                    <ReplayIcon sx={{ fontSize: 16 }} />
                </button>
                <button type="button" className="pc-ls-pbtn" onClick={toggle} disabled={!ws} title={playing ? 'Пауза' : 'Пуск'}>
                    {playing ? <PauseIcon sx={{ fontSize: 18 }} /> : <PlayArrowIcon sx={{ fontSize: 18 }} />}
                </button>
                <button type="button" className="pc-ls-btn pc-ls-btn--ico" title="Вперёд 15 с" onClick={() => seekBy(15)} disabled={!ws}>
                    <ForwardIcon sx={{ fontSize: 16 }} />
                </button>
                <span className="pc-ls-time">
                    <span>{mmss(time)}</span>{' '}
                    <span className="tot">/ {mmss(duration)}</span>
                </span>
                <div className="pc-ls-seg" role="tablist">
                    {SPEEDS.map((v) => (
                        <button
                            key={v}
                            type="button"
                            role="tab"
                            aria-selected={speed === v}
                            onClick={() => setSpeed(v)}
                        >
                            {v}×
                        </button>
                    ))}
                </div>
                <div className="pc-ls-vol">
                    <VolumeUpOutlinedIcon sx={{ fontSize: 16, color: 'var(--muted)' }} />
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={Math.round(volume * 100)}
                        onChange={(e) => setVolume(Number(e.target.value) / 100)}
                    />
                </div>
                <div className="pc-ls-marks">
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: 20,
                        padding: '0 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 500,
                        background: silences.length ? 'var(--danger-soft)' : 'var(--ok-soft)',
                        color: silences.length ? 'var(--danger)' : 'var(--ok)',
                        border: `1px solid ${silences.length ? 'var(--danger-line)' : 'var(--ok-line)'}`,
                    }}>
                        {silences.length ? `пауз дольше 5 с: ${silences.length}` : 'пауз нет'}
                    </span>
                    <button
                        type="button"
                        className={`pc-ls-btn pc-ls-btn--sm${flagged ? ' pc-ls-btn--pri' : ''}`}
                        onClick={onFlag}
                    >
                        <FlagOutlinedIcon sx={{ fontSize: 14 }} />
                        На разбор
                    </button>
                </div>
            </div>
        </>
    );
};

export default Playlist;
